import { useState } from "react";
import { motion, useScroll, useSpring, useMotionValueEvent } from "framer-motion";
import { Menu, X, ArrowRight, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { NAV, CONTACT } from "../../content/site";
import { scrollToId } from "./useLenis";
import { whatsappUrl, WhatsAppIcon } from "./WhatsApp";

export const Nav = () => {
  const { scrollY, scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 140, damping: 30 });
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <motion.div data-testid="scroll-progress" className="fixed left-0 right-0 top-0 z-[60] h-[3px] origin-left bg-[#C86D51]" style={{ scaleX: progress }} />
      <header
        data-testid="site-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
          scrolled || open ? "bg-[#FBF9F4]/90 py-3 shadow-[0_1px_0_#E2DDD0] backdrop-blur-md" : "bg-transparent py-5"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8">
          <button data-testid="nav-logo" onClick={() => go("top")} aria-label="Zum Seitenanfang">
            <Logo intro light={!(scrolled || open)} />
          </button>
          <nav className="hidden items-center gap-7 lg:flex">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`nav-link-${n.id}`}
                onClick={() => go(n.id)}
                className={`link-underline text-sm font-medium ${scrolled ? "text-[#1B281E]" : "text-[#FBF9F4]"}`}
              >
                {n.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button data-testid="nav-cta-button" onClick={() => go("kontakt")} className="btn-primary nav-cta">
              Unverbindlich anfragen <ArrowRight className="h-4 w-4" />
            </button>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((o) => !o)}
              aria-label="Menü"
              className={`grid h-11 w-11 place-items-center rounded-full lg:hidden ${scrolled || open ? "text-[#1B281E]" : "text-[#FBF9F4]"}`}
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        {open && (
          <div data-testid="nav-mobile-menu" className="mx-auto max-w-7xl px-5 pb-6 pt-4 lg:hidden">
            {NAV.map((n) => (
              <button key={n.id} data-testid={`mobile-nav-link-${n.id}`} onClick={() => go(n.id)} className="block w-full border-b border-[#E2DDD0] py-4 text-left font-display text-2xl text-[#1B281E]">
                {n.label}
              </button>
            ))}
          </div>
        )}
      </header>
      <div
        className={`sticky-cta fixed bottom-4 left-4 right-4 z-40 flex gap-2.5 transition-[opacity,transform] duration-500 ${
          scrolled ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-6 opacity-0"
        }`}
      >
        <button data-testid="mobile-sticky-cta" onClick={() => go("kontakt")} className="btn-primary min-w-0 flex-1 justify-center whitespace-nowrap py-4 shadow-xl">
          Unverbindlich anfragen
        </button>
        <a
          data-testid="mobile-whatsapp-button"
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Jonas per WhatsApp schreiben"
          className="grid h-[52px] w-[52px] shrink-0 place-items-center rounded-full bg-[#25D366] text-white shadow-xl transition-transform duration-300 active:scale-95"
        >
          <WhatsAppIcon className="h-6 w-6" />
        </a>
        <a
          data-testid="mobile-call-button"
          href={`tel:${CONTACT.phone}`}
          aria-label={`Jonas anrufen: ${CONTACT.phone}`}
          className="grid h-[52px] w-[52px] shrink-0 place-items-center rounded-full bg-[#C86D51] text-[#FBF9F4] shadow-xl transition-transform duration-300 active:scale-95"
        >
          <Phone className="h-5 w-5" />
        </a>
      </div>
    </>
  );
};
